<template>
  <card v-if="videoReasonSidePanelEditing" :has-settings="false" :has-subtitle="false" title="Video Reason" :auto-height="true" class="video-reason-card">
    <template #content>
      <ld-form without-content-wrapper v-if="availableVideoReasons.length" ref="form">
        <template #default>
          <div class="video-reason-content">
            <input-with-validation
              v-if="!videoReasonMultiSelect"
              v-model="selectedVideoReason"
              field-label="Video Reason"
              :rules="videoReasonRules"
              b-name="select"
              expanded>
              <option :value="null">N/A</option>
              <option v-for="reason in availableVideoReasons" :value="reason.id" :key="reason.id">
                {{ reason.name }}
              </option>
            </input-with-validation>
            <input-with-validation v-else field-label="Video Reason" :rules="videoReasonRules">
              <template #custom="{ handleChange }">
                <ld-taginput ref="videoReasonTaginput" v-model="selectedVideoReasons" :data="availableVideoReasons" field="name" @update:modelValue="handleChange">
                  <template #default="{ option }">
                    {{ option.name }}
                  </template>
                  <template #all-selected>
                    <p>All video reasons are selected</p>
                  </template>
                  <template #selected="{ tags }">
                    <b-tag :key="index" type="is-info" closable @close="removeVideoReason(index, $event)"
                      v-for="(tag, index) in tags">{{ tag.name }}</b-tag>
                  </template>
                  <template #not-found>
                    <p>No video reason found</p>
                  </template>
                </ld-taginput>
              </template>
            </input-with-validation>
          </div>
        </template>
      </ld-form>
      <p v-else class="p-4 has-text-centered">Please add or enable at least one video reason</p>
    </template>
    <template #headerUtils>
      <button class="button is-primary is-outlined mr-2" @click="save" v-if="availableVideoReasons.length">Save</button>
      <slot name="headerUtils"></slot>
    </template>
  </card>
</template>

<script>
import Card from "@/components/card/Card.vue"
import InputWithValidation from "@/components/validation/InputWithValidation.vue"
import LdTaginput from "@/components/LdTaginput.vue"
import LdForm from "@/components/LdForm.vue"
import to from "@/lib/to"

export default {
  name: "VideoReasonCard",
  components: { Card, InputWithValidation, LdTaginput, LdForm },
  props: {
    meetingId: {
      type: Number,
      required: true
    }
  },
  computed: {
    videoReasonMultiSelect() {
      return this.$store.state.currentHospital?.video_reason_multi_select === true
    },
    videoReasonSidePanelEditing() {
      return this.$store.state.currentHospital?.video_reason_side_panel_editing !== false
    },
    videoReasonRules() {
      return this.videoReasonRequired ? 'required' : ''
    },
    videoReasonRequired() {
      return this.$store.state.currentHospital?.video_reason_required !== false
    },
  },
  async mounted() {
    await this.fetchVideoReasons()
    await this.fetchMeeting()
  },
  methods: {
    async fetchVideoReasons() {
      const [err, response] = await to(this.$http.get('/video_reasons/dropdown'))
      if (!err) {
        this.availableVideoReasons = response.data
      }
    },
    async fetchMeeting() {
      const [err, response] = await to(this.$http.get(`/meetings/${this.meetingId}`))
      if (!err && response.data) {
        const meeting = response.data
        if (meeting.video_reasons && meeting.video_reasons.length > 0) {
          if (this.videoReasonMultiSelect) {
            const reasonIds = meeting.video_reasons.map(r => r.id)
            this.selectedVideoReasons = this.availableVideoReasons.filter(reason =>
              reasonIds.includes(reason.id)
            )
          } else {
            this.selectedVideoReason = meeting.video_reasons[0].id
          }
        }
      }
    },
    async save() {
      if (!this.$refs?.form?.$refs?.form?.meta.valid) {
        return
      }
      const videoReasonIds = this.videoReasonMultiSelect
        ? this.selectedVideoReasons.map(r => r.id)
        : [this.selectedVideoReason]

      const [err] = await to(this.$http.put(`/meetings/${this.meetingId}/video_reason`, {
        video_reason_ids: videoReasonIds
      }))

      if (!err) {
        this.$buefy.toast.open({
          message: 'Video reason updated successfully',
          type: 'is-success'
        })
      } else {
        this.$buefy.toast.open({
          message: 'Failed to update video reason',
          type: 'is-danger'
        })
      }
    },
    removeVideoReason(index, $event) {
      this.$refs.videoReasonTaginput.$refs.input.removeTag(index, $event)
    }
  },
  data() {
    return {
      availableVideoReasons: [],
      selectedVideoReason: null,
      selectedVideoReasons: []
    }
  }
}
</script>

<style scoped lang="scss">
.video-reason-card {
  :deep(.monitoring-card__content) {
    overflow: visible;
  }
}
</style>